/**
 * FikoSTEPDisaAktar — sahnedeki bir objeyi STEP (.step) dosyasina cevirip indirir.
 *
 * Genel API burada toplanir: FikoCizim* siniflari (STL/GLB/FBX/WRL/Grup...) yalnizca
 * bu dosyadan `fikoObjeyiStepIndir` cagirir; boru hattinin detaylarini bilmek zorunda
 * degildir.
 *
 * Bu dosya bilerek "yaprak" modul: hicbir FikoCizim* dosyasini import etmez, boylece
 * FikoCizimGLB/FikoCizimSTL buradan import edince dongu olusmaz.
 */
import * as THREE from 'three';
import { fikoStepMeshHazirla, FikoStepGeometriSecenek, FikoStepMeshVerisi } from './FikoStepGeometri';
import { fikoStepMetniUret, FikoStepModu, FikoStepSonuc } from './FikoStepYazici';
import { fikoDosyaIndir } from './FikoSTLDisaAktar';

export type { FikoStepModu };

export interface FikoStepDisaAktarSecenek extends FikoStepGeometriSecenek {
  isim?: string;
  yazar?: string;
  organizasyon?: string;
}

export interface FikoStepDisaAktarSonuc {
  blob: Blob;
  mesh: FikoStepMeshVerisi;
  yazim: FikoStepSonuc;
}

/** "ComfyUI_00004.glb" -> "ComfyUI_00004.step" */
export function fikoStepAdi(ad: string): string {
  const temiz = (ad || 'model').replace(/\.[^.\\/]+$/, '');
  return (temiz || 'model') + '.step';
}

/* ================================================================== */
/* Kullanici STEP ayarlari — tarayici localStorage'inda saklanir.       */
/* "Resimden 3D" dialogu ile AGACTAKI (STL/GLB/FBX/WRL sag-tik menusu)  */
/* disa aktarimlar AYNI anahtari kullanir; biri digerini de etkiler.    */
/* ================================================================== */

const STEP_AYAR_ANAHTARI = 'fikoStepAyarlari';

export interface FikoStepKullaniciAyarlari {
  /** 0 = sinirsiz (sadelestirme yapma). */
  hedefUcgenBrep: number;
  /** 0 = sinirsiz. */
  hedefUcgenTessellated: number;
  kaynakTolerans: number;
}

const STEP_AYAR_VARSAYILAN: FikoStepKullaniciAyarlari = {
  hedefUcgenBrep: 25000,
  hedefUcgenTessellated: 0,
  kaynakTolerans: 0.001
};

/** Kayitli STEP ayarlarini okur; hic kayit yoksa/bozuksa varsayilanlari doner. */
export function fikoStepAyarlariniOku(): FikoStepKullaniciAyarlari {
  try {
    const ham = localStorage.getItem(STEP_AYAR_ANAHTARI);
    if (!ham) return { ...STEP_AYAR_VARSAYILAN };
    const a = JSON.parse(ham);
    return {
      hedefUcgenBrep: typeof a?.hedefUcgenBrep === 'number' && a.hedefUcgenBrep >= 0
        ? a.hedefUcgenBrep : STEP_AYAR_VARSAYILAN.hedefUcgenBrep,
      hedefUcgenTessellated: typeof a?.hedefUcgenTessellated === 'number' && a.hedefUcgenTessellated >= 0
        ? a.hedefUcgenTessellated : STEP_AYAR_VARSAYILAN.hedefUcgenTessellated,
      kaynakTolerans: typeof a?.kaynakTolerans === 'number' && a.kaynakTolerans > 0
        ? a.kaynakTolerans : STEP_AYAR_VARSAYILAN.kaynakTolerans
    };
  } catch { return { ...STEP_AYAR_VARSAYILAN }; }
}

/** STEP ayarlarini localStorage'a yazar. */
export function fikoStepAyarlariniYaz(a: FikoStepKullaniciAyarlari): void {
  try { localStorage.setItem(STEP_AYAR_ANAHTARI, JSON.stringify(a)); } catch { /* yoksay */ }
}

/**
 * Agactaki bir ogenin sag-tik menusunden cagirilir: basit prompt'larla STEP
 * ayarlarini sorar ve kaydeder. "Resimden 3D" dialogundaki panelle AYNI
 * localStorage anahtarini kullandigi icin degisiklik her iki yeri de etkiler.
 */
export function fikoStepAyarlariniDuzenle(): void {
  const mevcut = fikoStepAyarlariniOku();

  const brepStr = prompt('B-Rep hedef ucgen sayisi (0 = sinirsiz):', String(mevcut.hedefUcgenBrep));
  if (brepStr === null) return;
  const tessStr = prompt('Tessellated hedef ucgen sayisi (0 = sinirsiz):', String(mevcut.hedefUcgenTessellated));
  if (tessStr === null) return;
  const toleransStr = prompt('Kaynak toleransi (mm):', String(mevcut.kaynakTolerans));
  if (toleransStr === null) return;

  const brep = Math.max(0, Math.round(Number(brepStr)) || 0);
  const tess = Math.max(0, Math.round(Number(tessStr)) || 0);
  const toleransSayi = Number(toleransStr);
  const tolerans = toleransSayi > 0 ? toleransSayi : mevcut.kaynakTolerans;

  fikoStepAyarlariniYaz({ hedefUcgenBrep: brep, hedefUcgenTessellated: tess, kaynakTolerans: tolerans });
  alert('STEP ayarlari kaydedildi.');
}

/**
 * Varsayilan hazirlama secenekleri. B-Rep modunda kati (solid) sonuc sansini
 * artirmak icin cimdik onarimi ve uyarlanabilir sadelestirme acik gelir;
 * tessellated modda bunlar gerekmez ama zarar da vermez (mesh onceden
 * temizlenmis olur, ust taraf indirme fonksiyonlari zaten moda gore ayarlar).
 *
 * hedefUcgen/kaynakTolerans icin oncelik: acikca gecilen `secenek` > kullanicinin
 * kayitli STEP ayarlari (bkz. fikoStepAyarlariniOku) > sabit varsayilan.
 */
function varsayilanSecenek(mod: FikoStepModu, secenek?: FikoStepDisaAktarSecenek): FikoStepGeometriSecenek {
  const kullanici = fikoStepAyarlariniOku();
  const hedefUcgenKullanici = mod === 'brep' ? kullanici.hedefUcgenBrep : kullanici.hedefUcgenTessellated;

  return {
    kaynakTolerans: kullanici.kaynakTolerans,
    minUcgenAlan: 1e-6,
    cimdikOnar: mod === 'brep',
    onarimTuru: 3,
    normalUret: true,
    uyarlanabilirSadelestirme: true,
    enFazlaDeneme: 4,
    denemeCarpani: 1.4,
    // B-Rep'te her ucgen ~9 STEP satirina karsilik gelir (nokta+yon+yerlesim+
    // duzlem+poly_loop+face_outer_bound+face_surface...); AI'dan gelen 500k-700k
    // ucgenlik ham aglarda bu, tek senkron JS dongusunde milyonlarca satir demek
    // olur ve tarayiciyi/CAD programini dakikalarca kilitleyebilir. Kullanici
    // kayitli ayarlarda "sinirsiz" (0) secmedigi surece makul bir tavana
    // sadelestir; Tessellated modda ucgen basina 1 satir oldugu icin varsayilan
    // sinirsizdir.
    hedefUcgen: hedefUcgenKullanici > 0 ? hedefUcgenKullanici : undefined,
    ...secenek
  };
}

/**
 * Sahnedeki bir objeyi (FikoCizim, Grup, Mesh...) STEP verisine cevirir.
 * Geometri hazirlama (kaynakla/temizle/onar/normaller) ve STEP metnine
 * yazma adimlarinin ikisini de yapar; sonucta hem ham mesh istatistikleri
 * hem de olusan Blob doner.
 */
export async function fikoObjeyiStepVeri(
  kok: THREE.Object3D,
  mod: FikoStepModu,
  secenek?: FikoStepDisaAktarSecenek
): Promise<FikoStepDisaAktarSonuc> {
  if (!kok) throw new Error('STEP icin obje yok.');

  const hazirlamaSecenegi = varsayilanSecenek(mod, secenek);
  const mesh = await fikoStepMeshHazirla(kok, hazirlamaSecenegi);

  if (mesh.ucgenSayisi === 0) {
    throw new Error("Bu ogede STEP'e aktarilacak yuzey (mesh) bulunamadi.");
  }

  const isim = (secenek?.isim || (kok as any)?.name || 'model').replace(/\.[^.\\/]+$/, '');
  const yazim = fikoStepMetniUret(mesh, mod, {
    isim,
    yazar: secenek?.yazar,
    organizasyon: secenek?.organizasyon
  });

  const blob = new Blob([yazim.metin], { type: 'application/step' });
  return { blob, mesh, yazim };
}

/** Objeyi dogrudan .step olarak indirir (B-Rep ya da AP242 Tessellated). */
export async function fikoObjeyiStepIndir(
  kok: THREE.Object3D,
  mod: FikoStepModu,
  ad?: string,
  secenek?: FikoStepDisaAktarSecenek
): Promise<FikoStepDisaAktarSonuc> {
  const sonuc = await fikoObjeyiStepVeri(kok, mod, secenek);
  fikoDosyaIndir(sonuc.blob, fikoStepAdi(ad || (kok as any)?.name || 'model'));
  return sonuc;
}
